import { Box, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';
import { ExerciseDistribution } from './ExerciseDistribution';
import { DivergingExerciseStats, ExerciseStatsMode } from './TrainingStats';
import { useEffect, useState } from 'react';
import { CompletionDataExecutionTypeEnum, TrainingHistory, TrainingHistoryEntry } from '@/generated';
import { FilterMode, OverallStats, TrainingRating } from './OverallStats';
import { Moment } from 'moment';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

type TrainingStatsProps = {
  trainingHistory?: TrainingHistory;
  setSelectedEntryId: (id: string) => void;
  setSelectedEntry: (entry?: TrainingHistoryEntry) => void;
};

const distributionModeStorageKey = 'training-stats-distribution-mode';
const distributionModeOrder: ExerciseStatsMode[] = [
  CompletionDataExecutionTypeEnum.Reps,
  CompletionDataExecutionTypeEnum.Duration,
  CompletionDataExecutionTypeEnum.Intervals,
];
const selectableExerciseStatsModes: ExerciseStatsMode[] = [...distributionModeOrder, 'total'];

export const TrainingStatsTab = (props: TrainingStatsProps) => {
  const { trainingHistory, setSelectedEntryId, setSelectedEntry } = props;
  const [selectedEntry, setSelectedEntryState] = useState<TrainingHistoryEntry>();

  const [date, setDate] = useState<Moment | null>(moment());
  const [mode, setMode] = useState<ExerciseStatsMode>(() => {
    const storedMode = localStorage.getItem(distributionModeStorageKey) as ExerciseStatsMode | null;
    return storedMode && selectableExerciseStatsModes.includes(storedMode) ? storedMode : 'total';
  });
  const [filterMode, setFilterMode] = useState<FilterMode>(FilterMode.time);

  useEffect(() => {
    const latestEntry = trainingHistory?.historyEntries.reduce<TrainingHistoryEntry | undefined>((latest, entry) => {
      if (!latest) return entry;
      return new Date(entry.trainingDateIsoString).getTime() > new Date(latest.trainingDateIsoString).getTime() ? entry : latest;
    }, undefined);

    setSelectedEntryState(latestEntry);
    setSelectedEntry(latestEntry);
    if (latestEntry) setDate(moment(latestEntry.trainingDateIsoString));
  }, [trainingHistory, setSelectedEntry]);

  const selectEntry = (entry?: TrainingHistoryEntry) => {
    setSelectedEntryState(entry);
    setSelectedEntry(entry);
  };

  useEffect(() => {
    if (!selectedEntry) return;

    setMode('total');
    localStorage.setItem(distributionModeStorageKey, 'total');
    setSelectedEntryId(selectedEntry.entryId);
  }, [selectedEntry, setSelectedEntryId]);

  const selectDistributionMode = (selectedMode: ExerciseStatsMode) => {
    setMode(selectedMode);
    localStorage.setItem(distributionModeStorageKey, selectedMode);
  };

  return (
    <Stack sx={{ gap: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '220px 160px 240px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <DatePicker
          value={date}
          onChange={(selectedDate) => {
            setDate(selectedDate);
          }}
          disableFuture
          label="Selected Month"
          views={['month', 'year']}
          sx={{ width: '100%' }}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel id="filter-select-label">Mode</InputLabel>
          <Select
            labelId="filter-select-label"
            id="filter-select"
            label="Mode"
            value={filterMode}
            onChange={(event) => setFilterMode(event.target.value as FilterMode)}
          >
            <MenuItem value={FilterMode.time}>Time</MenuItem>
            <MenuItem value={FilterMode.volume}>Volume</MenuItem>
          </Select>
        </FormControl>
        <Paper variant="outlined" sx={{ minWidth: 240, px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
          <Stack>
            <Typography variant="caption" color="text.secondary">
              Selected training
            </Typography>
            <Typography variant="body1">
              {selectedEntry ? moment(selectedEntry.trainingDateIsoString).format('DD.MM.YYYY HH:mm') : 'No training selected'}
            </Typography>
          </Stack>
        </Paper>
        {selectedEntry && <TrainingRating selectedEntry={selectedEntry} />}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 340px' },
          width: '100%',
          minWidth: 0,
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <OverallStats filterMode={filterMode} date={date} trainingHistory={trainingHistory} selectedEntry={selectedEntry} setSelectedEntry={selectEntry} />
        </Box>
        {selectedEntry && (
          <Box>
            <ExerciseDistribution selectedEntry={selectedEntry} selectedMode={mode} onClick={selectDistributionMode} />
          </Box>
        )}
      </Box>

      {selectedEntry && <DivergingExerciseStats selectedEntry={selectedEntry} mode={mode} />}
    </Stack>
  );
};
